import { DataLayer, IDataLayer } from './tealium/data_layer';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    utag: {
      link: (data: IDataLayer, callback?: () => void) => void;
      view: (data: IDataLayer, callback?: () => void) => void;
    };
    utag_cfg_ovrd: IUtagCfgOverride;
    utag_data: IDataLayer;
    page_data: {
      'analytics-datalayer': IDataLayer;
    };
  }
}

export enum ActionType {
  LINK = 'link',
  VIEW = 'view'
}

export interface ITealiumSettings {
  tealiumProfile: string;
  tealiumUrl?: string;
  tealiumEnv: string;
}

interface IUtagCfgOverride {
  settings?: ITealiumSettings;
  path?: string;
  noview?: boolean;
}

const tealium = {
  dataLayer: {} as DataLayer,
  tealiumUrl: '' as string,
  tealiumProfile: '' as string,
  tealiumEnv: '' as string,
  loaded: false as boolean,
  ready: false as boolean,

  async init({ tealiumProfile, tealiumEnv, tealiumUrl }: ITealiumSettings) {
    this.tealiumProfile = tealiumProfile;
    this.tealiumEnv = tealiumEnv;
    this.tealiumUrl = tealiumUrl || 'tags.tiqcdn.com';
    window.utag_cfg_ovrd = window.utag_cfg_ovrd || {};

    if (!this.tealiumEnv || !this.tealiumProfile) {
      return;
    }

    if (window.location.pathname.endsWith('/checkout')) {
      this.disableDefaultPageView();
    }

    const hostPrefix = window.location.hostname.split('.')[0];
    if (hostPrefix === 'pincer' || hostPrefix === 'wwwtmp' || hostPrefix === 'mtmp') {
      this.tealiumEnv = 'qa';
    }

    this.dataLayer = new DataLayer();
    await this.dataLayer.load();

    this.load();
  },

  load(): void {
    window.utag_cfg_ovrd.path = `//${this.tealiumUrl}/utag/esteelauder/${this.tealiumProfile}/${this.tealiumEnv}/`;

    const script = document.createElement('script');
    script.src = window.utag_cfg_ovrd.path + 'utag.js';
    script.type = 'text/javascript';
    script.async = true;

    const onLoadHandler = () => {
      if (!this.loaded) {
        this.loaded = true;
        setTimeout(() => {
          this.ready = true;
          window.dispatchEvent(new CustomEvent(`track:ready`));
          this.dataLayer.processEvents();
        }, 500);
      }
    };

    script.addEventListener('readystatechange', function () {
      if (this.readyState === 'complete' || this.readyState === 'loaded') {
        onLoadHandler();
      }
    });

    script.addEventListener('load', onLoadHandler);

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  },

  disableDefaultPageView(): void {
    window.utag_cfg_ovrd.noview = true;
  },

  refreshPageData: function () {
    return window.page_data;
  },

  tealiumAPI(type: ActionType, dataLayer: IDataLayer, callback?: void): void {
    if (typeof window.utag !== 'undefined') {
      if (callback) {
        window.utag[type](dataLayer, callback);
      } else {
        window.utag[type](dataLayer);
      }
    }
  },

  evtLink(dataLayer: IDataLayer, callback?: void) {
    this.tealiumAPI(ActionType.LINK, dataLayer, callback);
  },

  evtView(dataLayer: IDataLayer, callback?: void) {
    // TODO: should this update the datalayer? some view events seem to do this, some don't - investigate
    this.tealiumAPI(ActionType.VIEW, dataLayer, callback);
  }
};

export default tealium;
